'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import { EmailInputForm } from '@shared-components/Newsletter/EmailInputForm/EmailInputForm';
import type { NewsletterStrings } from '@shared-components/Newsletter/EmailInputForm/EmailInputForm.entity';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { optInAction } from '../../Actions/OptIn.action';
import { resolveAction } from '../../Actions/Resolve.action';
import { OPT_IN_URLS } from '../../Constants/OptIn.constants';
import { useOptIn } from '../../Hooks/UseOptIn';
import { generateSubmitButtonTracking } from '../../Tracking/NewsletterOptIn.tracking';

interface NewsletterOptInFormProps {
  strings: NewsletterStrings;
  tenant?: Tenant;
}

export const NewsletterOptInForm = ({ strings, tenant }: NewsletterOptInFormProps) => {
  const user_Id = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const { optInData, setOptIn } = useOptIn();

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleSubmit = async (email: string) => {
    const url = OPT_IN_URLS.uk;
    if (!url) return;

    try {
      const { user_id: userId } = await resolveAction(user_Id!, { email }, optInData);
      const response = await optInAction(
        { email, userId, user_Id, ...(tenant && { tenant }) },
        strings,
      );
      if (response.error) return response.error;

      const { event, context } = generateSubmitButtonTracking({
        searchParams,
        userId: user_Id,
        formState,
        ctaType: 'button',
        ctaWording: strings.signUpCta,
        ctaUrl: url,
        ctaLocation: 'footer',
      });
      trackCustomEvent({ event, context });
      setOptIn(email);
    } catch (_error) {
      return strings.emailError;
    }
  };

  if (!hasMounted) return null;

  return (
    <EmailInputForm strings={strings} isSubscribed={!!optInData.email} onSubmit={handleSubmit} />
  );
};
